import React, { useEffect } from "react";
import { Paper, useMediaQuery, useTheme } from "@mui/material";
import { ChatSurface } from "./ChatSurface";
import { useAiPanelOpen, useAiPanelFullscreen } from "../../hook/ai-panel";

export const AI_PANEL_WIDTH = 420;

// A phone-width viewport: the breakpoint the side nav already closes at
// (hook/sideNav.tsx useInitSideNavOpenListener). There the panel covers the screen.
export function useAiPanelNarrow(): boolean {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("sm"));
}

export const AiPanel: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useAiPanelOpen();
  const [fullscreen, setFullscreen] = useAiPanelFullscreen();
  const narrow = useAiPanelNarrow();
  const lockPage = open && narrow;

  // agent-chat-speed Decision 8 (U.1): on a phone, a touch scroll that reaches the end of the
  // message list chained to the page behind the fixed panel, and Mobile Safari's collapsing toolbar
  // then moved the panel. While the panel is open on a narrow viewport the page cannot scroll;
  // the inline values that were there before are restored on close.
  useEffect(() => {
    if (!lockPage) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.overscrollBehavior = prev.bodyOverscroll;
    };
  }, [lockPage]);

  if (!open) return null;

  return (
    <Paper
      data-testid="ai-panel"
      elevation={4}
      sx={{
        position: "fixed",
        right: 0,
        top: 0,
        bottom: 0,
        // Sized to the dynamic viewport where supported (Safari 15.4+), so the mobile toolbar
        // showing or hiding resizes the panel instead of moving it.
        "@supports (height: 100dvh)": { bottom: "auto", height: "100dvh" },
        width: fullscreen || narrow ? "100vw" : AI_PANEL_WIDTH,
        maxWidth: "100vw",
        zIndex: theme.zIndex.drawer + 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        overscrollBehavior: "contain",
        borderLeft: `1px solid ${theme.palette.divider}`,
        transition: "width 0.2s ease",
      }}
    >
      <ChatSurface
        fullscreen={fullscreen}
        onToggleFullscreen={() => setFullscreen(!fullscreen)}
        onClose={() => setOpen(false)}
      />
    </Paper>
  );
};
